import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Truck, Star, MapPin, Loader2, ArrowRight, Sparkles,
  ChevronRight, Send, Calendar, Users, CheckCircle,
} from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbLD, collectionPageLD, itemListLD, faqLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORTED_CITIES = [
  { slug: 'denver',       name: 'Denver',       state: 'CO' },
  { slug: 'las-vegas',    name: 'Las Vegas',    state: 'NV' },
  { slug: 'los-angeles',  name: 'Los Angeles',  state: 'CA' },
  { slug: 'charlotte',    name: 'Charlotte',    state: 'NC' },
];

const EVENT_TYPES = [
  'Wedding', 'Corporate event', 'Birthday party',
  'Private party', 'Festival', 'Pop-up', 'Other',
];

const FAQS = [
  { q: 'How far in advance do I need to book?',
    a: 'Most food trucks book up 4–8 weeks out for weekends. We can usually pull together short-notice bookings (3–14 days out) for a small rush fee.' },
  { q: 'How many guests can one truck serve?',
    a: 'A single truck typically serves 100–150 guests in a 90-minute window. For larger headcounts we recommend booking 2+ trucks or a hybrid setup.' },
  { q: 'Do you handle permits, generators, and water?',
    a: 'Yes — every truck on Eventism is licensed, insured, and brings its own power/water. We coordinate any site-specific permits required by your venue.' },
  { q: 'What does it cost?',
    a: 'Most food trucks charge per-guest with a minimum spend (typically $20–$35 per person, $1,500 minimum). Drinks, dessert trucks, and specialty cuisines vary. Get an instant quote below.' },
];

/**
 * /food-trucks and /:city/food-trucks
 * Shared component — the optional `:city` URL slug filters the directory
 * and personalizes the page title.
 */
export default function FoodTrucksLanding() {
  const { city: cityParam } = useParams();
  const navigate = useNavigate();
  const city = SUPPORTED_CITIES.find((c) => c.slug === cityParam) || null;
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = city?.slug ? { city: city.slug } : {};
      const r = await axios.get(`${API}/public/food-trucks`, { params });
      setTrucks(r.data?.food_trucks || []);
    } catch (_) { setTrucks([]); }
    finally { setLoading(false); }
  }, [city]);

  useEffect(() => { load(); }, [load]);

  const heading = city
    ? `Food trucks in ${city.name}`
    : 'Hire a food truck for your event';
  const subheading = city
    ? `Curated, fully-licensed food trucks ready to cater your next event in ${city.name}.`
    : 'Licensed, insured food trucks across 4 cities. Instant quotes, real availability, zero ghosting.';

  // ---- JSON-LD ------------------------------------------------------------
  const breadcrumb = breadcrumbLD([
    { name: 'Home', url: '/' },
    { name: 'Food Trucks', url: '/food-trucks' },
    ...(city ? [{ name: city.name, url: `/${city.slug}/food-trucks` }] : []),
  ]);
  const collection = collectionPageLD(heading, subheading);
  const itemList = itemListLD(
    trucks.slice(0, 30).map((t) => ({
      name: t.name,
      url: t.url,
      image: t.image_url,
    })),
    heading,
  );
  const faqs = faqLD(FAQS);

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="food-trucks-landing">
      <SEO
        title={
          city
            ? `Food Trucks in ${city.name} — Hire for Events | Eventism`
            : 'Food Truck Catering for Events | Eventism'
        }
        description={subheading}
        canonical={city ? `/${city.slug}/food-trucks` : '/food-trucks'}
        keywords={`food trucks${city ? `, ${city.name} food trucks` : ''}, food truck catering, event food trucks, hire food truck, mobile catering`}
        structuredData={[collection, breadcrumb, itemList, faqs]}
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 space-y-4">
        <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-orange-400 flex items-center gap-2">
          <Truck className="h-3.5 w-3.5" /> Marketplace · Food trucks
        </p>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Space_Grotesk'] leading-[1.05]"
          data-testid="food-trucks-h1"
        >
          {city ? (
            <>Food trucks in <span className="bg-gradient-to-r from-orange-400 to-rose-400 bg-clip-text text-transparent">{city.name}</span></>
          ) : (
            <>Hire a <span className="bg-gradient-to-r from-orange-400 to-rose-400 bg-clip-text text-transparent">food truck</span> for your event.</>
          )}
        </h1>
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">{subheading}</p>

        <div className="flex flex-wrap gap-3 pt-2">
          <a href="#quote" data-testid="food-trucks-hero-cta">
            <Button className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white h-11 px-6 font-bold">
              <Sparkles className="h-4 w-4 mr-2" /> Get instant quote
            </Button>
          </a>
          <Link to="/partners/join?category=food_truck" data-testid="food-trucks-list-cta">
            <Button variant="outline" className="h-11 px-6">
              <Truck className="h-4 w-4 mr-2" /> Are you a food truck? List with us
            </Button>
          </Link>
        </div>

        {!city && (
          <div className="flex flex-wrap items-center gap-2 pt-3">
            <span className="text-xs text-muted-foreground">Or browse by city:</span>
            {SUPPORTED_CITIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => navigate(`/${c.slug}/food-trucks`)}
                className="text-xs underline decoration-dotted hover:text-orange-400"
                data-testid={`food-trucks-city-${c.slug}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Directory */}
      <section className="max-w-6xl mx-auto px-4 mt-10 space-y-4">
        <div className="flex items-end justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Truck className="h-5 w-5 text-orange-400" />
            {city ? `${city.name} food trucks` : 'Featured food trucks'}
          </h2>
          <span className="text-xs text-muted-foreground" data-testid="food-trucks-count">
            {loading ? '…' : `${trucks.length} truck${trucks.length === 1 ? '' : 's'}`}
          </span>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-orange-400" />
          </div>
        ) : trucks.length === 0 ? (
          <Card className="border-dashed border-2 border-orange-500/30 bg-orange-500/5" data-testid="food-trucks-empty">
            <CardContent className="p-8 text-center space-y-3">
              <Truck className="h-10 w-10 text-orange-400 mx-auto" />
              <h3 className="text-xl font-bold">We're curating top food trucks now</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Our marketplace is launching{city ? ` in ${city.name}` : ''} this season. Drop your event details below
                and we'll match you with the right truck within 24 hours — or list your truck with us.
              </p>
              <div className="flex flex-wrap justify-center gap-2 pt-2">
                <a href="#quote">
                  <Button className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white">
                    Get instant quote <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
                <Link to="/partners/join?category=food_truck">
                  <Button variant="outline">List your truck</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {trucks.map((t) => <FoodTruckCard key={t.id} truck={t} />)}
          </div>
        )}
      </section>

      {/* Quote form */}
      <QuoteSection cityHint={city?.slug} />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 mt-16 space-y-4" data-testid="food-trucks-faq">
        <h2 className="text-2xl font-bold">Frequently asked</h2>
        <div className="space-y-3">
          {FAQS.map((f) => (
            <Card key={f.q} className="border-border">
              <CardContent className="p-5">
                <h3 className="font-bold flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 mt-1 text-orange-400 shrink-0" />
                  {f.q}
                </h3>
                <p className="text-sm text-muted-foreground mt-2 ml-6">{f.a}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

function FoodTruckCard({ truck }) {
  return (
    <Link to={truck.url} data-testid={`food-truck-card-${truck.id}`} className="block group">
      <Card className="border-border hover:border-orange-500/40 transition-colors h-full overflow-hidden">
        {truck.image_url && (
          <div className="aspect-video bg-card overflow-hidden">
            <img src={truck.image_url} alt={truck.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          </div>
        )}
        <CardContent className="p-3 space-y-1">
          <p className="font-bold truncate">{truck.name}</p>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            {truck.city && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {String(truck.city).replace(/\b\w/g, (m) => m.toUpperCase())}
              </span>
            )}
            {truck.rating != null && (
              <span className="flex items-center gap-1 text-amber-300 font-medium">
                <Star className="h-3 w-3 fill-amber-300" />
                {Number(truck.rating).toFixed(1)}
              </span>
            )}
          </div>
          {truck.blurb && <p className="text-xs text-muted-foreground line-clamp-2">{truck.blurb}</p>}
          {truck.price_from != null && (
            <p className="text-xs font-semibold text-orange-300">From ${truck.price_from}</p>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function QuoteSection({ cityHint }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    event_type: 'Corporate event',
    event_date: '', guest_count: 100,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/leads`, {
        client_name: form.name,
        client_email: form.email,
        client_phone: form.phone,
        event_type: form.event_type.toLowerCase().replace(/\s+/g, '_'),
        event_date: form.event_date || null,
        guest_count: Number(form.guest_count) || 0,
        notes: `Food truck quote request. ${form.notes}`,
        source: cityHint ? `food-trucks-${cityHint}` : 'food-trucks',
        city: cityHint || '',
      });
      setSent(true);
      toast.success('Quote request sent — we\'ll reach out within 24 hours.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <section id="quote" className="max-w-3xl mx-auto px-4 mt-16" data-testid="food-trucks-quote-sent">
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-8 text-center space-y-3">
            <CheckCircle className="h-12 w-12 text-emerald-400 mx-auto" />
            <h3 className="text-2xl font-bold">Quote request received</h3>
            <p className="text-muted-foreground">
              Our team will email you a curated shortlist of food trucks within 24 hours.
              Check your inbox at <strong>{form.email}</strong>.
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section id="quote" className="max-w-3xl mx-auto px-4 mt-16" data-testid="food-trucks-quote">
      <Card className="border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-rose-500/5">
        <CardContent className="p-6 sm:p-8 space-y-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] font-bold text-orange-400">
              Instant quote
            </p>
            <h2 className="text-3xl font-bold mt-1">Tell us about your event</h2>
            <p className="text-sm text-muted-foreground mt-1">
              We'll match you with the right food truck — usually 3 options within 24 hours, no commitments.
            </p>
          </div>

          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
            <Field label="Your name *">
              <Input
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                required data-testid="quote-name"
              />
            </Field>
            <Field label="Email *">
              <Input
                type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required data-testid="quote-email"
              />
            </Field>
            <Field label="Phone">
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="quote-phone"
              />
            </Field>
            <Field label="Event type">
              <select
                value={form.event_type}
                onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm"
                data-testid="quote-event-type"
              >
                {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Event date">
              <Input
                type="date" value={form.event_date}
                onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                data-testid="quote-date"
              />
            </Field>
            <Field label="Guest count">
              <Input
                type="number" min="1" value={form.guest_count}
                onChange={(e) => setForm({ ...form, guest_count: e.target.value })}
                data-testid="quote-guests"
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Anything else? (cuisine preferences, venue notes)">
                <Textarea
                  rows={3} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="quote-notes"
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-bold"
                data-testid="quote-submit-btn"
              >
                {submitting
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                  : <><Send className="h-4 w-4 mr-2" /> Get my quote</>}
              </Button>
            </div>
          </form>
          <div className="flex items-center gap-4 text-[11px] text-muted-foreground pt-2 border-t border-border">
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Quote in 24h</span>
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> 10–10k guests</span>
            <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Licensed + insured</span>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase font-bold text-muted-foreground tracking-wide">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
